import { getApiBaseUrl } from './apiBaseUrl';
import { captureUiError } from './errorTracking';
import { recordApiRequest } from './applicationMetrics';

const DEFAULT_FETCH_TIMEOUT_MS = 10000;

export const fetchWithTimeout = async (url, options = {}, timeoutMs = DEFAULT_FETCH_TIMEOUT_MS) => {
  let correlatedOptions = options;
  let requestId;
  let ownedApi = false;
  try {
    ownedApi = new URL(url, window.location.href).origin === new URL(getApiBaseUrl()).origin;
    if (ownedApi) {
      const headers = new Headers(options.headers);
      requestId = headers.get('X-Request-Id') || crypto.randomUUID();
      headers.set('X-Request-Id', requestId);
      correlatedOptions = { ...options, headers };
    }
  } catch {
    /* Optional diagnostics must not prevent fetch. */
  }
  const controller = new AbortController();
  const externalSignal = options.signal;
  let didTimeout = false;

  if (externalSignal?.aborted) {
    throw new DOMException('Request aborted', 'AbortError');
  }

  const abortRequest = () => controller.abort();
  if (externalSignal) {
    externalSignal.addEventListener('abort', abortRequest, { once: true });
  }

  const timeoutId = window.setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, timeoutMs);

  const requestClock =
    // oxlint-disable-next-line anti-slop/no-runtime-typeof -- Feature-detect the optional browser timing API.
    typeof window.performance?.now === 'function'
      ? window.performance.now.bind(window.performance)
      : Date.now;
  const startedAt = requestClock();
  try {
    const response = await fetch(url, {
      ...correlatedOptions,
      signal: controller.signal,
    });
    if (ownedApi) {
      const outcome =
        response.status >= 500
          ? 'server_error'
          : response.status >= 400
            ? 'client_error'
            : 'success';
      recordApiRequest(options.method || 'GET', outcome, requestClock() - startedAt);
    }
    if (ownedApi && response.status >= 500)
      captureUiError(new Error('HTTP failure'), 'http.request', {
        status: response.status,
        request_id: response.headers?.get('X-Request-Id') || requestId,
      });
    return response;
  } catch (error) {
    if (ownedApi) {
      const outcome = externalSignal?.aborted
        ? 'cancelled'
        : didTimeout
          ? 'timeout'
          : 'network_error';
      recordApiRequest(options.method || 'GET', outcome, requestClock() - startedAt);
    }
    if (ownedApi && !externalSignal?.aborted)
      captureUiError(new Error('Request failed'), 'http.request', {
        stage: didTimeout ? 'timeout' : 'network',
        request_id: requestId,
      });
    if (didTimeout && error?.name === 'AbortError') {
      throw new DOMException(`Request timed out after ${timeoutMs}ms`, 'TimeoutError');
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
    if (externalSignal) {
      externalSignal.removeEventListener('abort', abortRequest);
    }
  }
};

export default fetchWithTimeout;
